import { InfoOutlineIcon } from '@chakra-ui/icons';
import {
    Box,
    Button,
    Center,
    Checkbox,
    Divider,
    Flex,
    FormControl,
    IconButton,
    Image,
    Input,
    Link,
    Radio,
    RadioGroup,
    Skeleton,
    Spinner,
    Text,
    Textarea,
    Tooltip,
    useColorModeValue,
    useDisclosure,
    useToast,
} from '@chakra-ui/react';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import ReplaceDriveModal from 'components/drive/ReplaceDriveModal';
import { useEffect, useRef, useState } from 'react';
import { useDropzone } from "react-dropzone";
import { FiEye } from 'react-icons/fi';
import { useDispatch } from 'react-redux';
import { useGenerateBackgroundImageMutation, useGenerateBackgroundPromptMutation, useGenerateCreativeVariationsMutation, useGeneratePromptMutation, useGetBackgroundImageStatusQuery, useRemoveBackgroundMutation } from "store/aiHub/aiGenerationApi";
import { useUploadImageMutation } from 'store/driveUpload/uploadApi';
import { setFiles, setIsWidgetOpen, updateUploadStatus } from "store/driveUpload/uploadSlice";
import { base64ToFile, overlayImages, truncateName, urlToFile } from "utils/functions";


const MAX_IMAGE_UPLOAD_SIZE_MB = 30; // Max size for images
const MAX_VIDEO_UPLOAD_SIZE_GB = 4; // Max size for videos
const MAX_IMAGE_UPLOAD_SIZE_BYTES = MAX_IMAGE_UPLOAD_SIZE_MB * 1024 * 1024;
const MAX_VIDEO_UPLOAD_SIZE_BYTES = MAX_VIDEO_UPLOAD_SIZE_GB * 1024 * 1024 * 1024;

function AiImageModal({ selectedAdAccounts, onClose, onUploadComplete }) {

    const dispatch = useDispatch();
    const toast = useToast();
    const { isOpen: isDriveOpen, onOpen: onDriveOpen, onClose: onDriveClose } = useDisclosure();
    const [activeGenerationType, setActiveGenerationType] = useState('');
    const [backgroundGenerationPrompt, setBackgroundGenerationPrompt] = useState('');
    const [creativeGenerationPrompt, setCreativeGenerationPrompt] = useState('');
    const [uploadedImage, setUploadedImage] = useState({});
    const [backgroundGenerationResults, setBackgroundGenerationResults] = useState([]);
    const [creativeGenerationResults, setCreativeGenerationResults] = useState([]);
    const [isCreativesGenerating, setIsCreativesGenerating] = useState(false);
    const [isBgCreativesGenerating, setBgIsCreativesGenerating] = useState(false);
    const [isBgLoading, setIsBgLoading] = useState(false);
    const [hasBgEdit, setBgHasEdit] = useState(false);
    const [hasVariationEdit, setVariationHasEdit] = useState(false);
    const [selectedCreativeImages, setSelectedCreativeImages] = useState([]);
    const [selectedBackgroundImages, setSelectedBackgroundImages] = useState([]);
    const [uploadType, setUploadType] = useState('');
    const [localFiles, setLocalFiles] = useState([]);
    const [backgroundImageIds, setBackgroundImageIds] = useState([]);
    const [baseImage, setBaseImage] = useState(null);
    const [creativeFiles, setCreativeFiles] = useState([]);
    const [backgroundFiles, setBackgroundFiles] = useState([]);
    const dropZoneBG = useColorModeValue('gray.50', 'gray.500')

    const [uploadImage, { isLoading: isImageLoading }] = useUploadImageMutation();
    const [generatePrompts, { isLoading: ispromptGenerating }] = useGeneratePromptMutation();
    const [generateCreativeVariations, { isLoading: isCreativesgenerating }] = useGenerateCreativeVariationsMutation();
    const [removeBackground, { isLoading: isBgremoving }] = useRemoveBackgroundMutation();
    const [generateBackgroundPrompt, { isLoading: isBgpromptGenerating }] = useGenerateBackgroundPromptMutation();
    const [generateBackgroundImages, { isLoading: isBgVariationsGenerating }] = useGenerateBackgroundImageMutation();
    const MAX_ATTEMPTS = 3;
    const POLL_INTERVAL = 2000; // 2 seconds

    const {
        data: backgroundImagesStatus,
        isLoading: isBgImagesGenerating,
        isSuccess,
        isError,
        refetch,
    } = useGetBackgroundImageStatusQuery(
        backgroundImageIds.length > 0 ? backgroundImageIds : skipToken
    );

    const attemptRef = useRef(1);

    useEffect(() => {
        let timeoutId;

        const shouldRetry = () => {
            if (!backgroundImagesStatus || backgroundImagesStatus.length === 0) return true;

            const validCount = backgroundImagesStatus.filter(item => item && item.id && item.url).length;
            return validCount < backgroundImageIds.length;
        };

        if (backgroundImagesStatus && isSuccess && baseImage) {
            const validData = backgroundImagesStatus.filter(item => item && item.id && item.url);

            if (shouldRetry() && attemptRef.current < MAX_ATTEMPTS) {
                timeoutId = setTimeout(() => {
                    attemptRef.current += 1;
                    refetch();
                }, POLL_INTERVAL);
            } else {
                // Final processing
                Promise.all(
                    validData.map(({ id, url }) =>
                        overlayImages(url, baseImage).then(compositedImage => ({ id, url: compositedImage }))
                    )
                ).then((compositedResults) => {
                    setBackgroundGenerationResults(compositedResults);
                    setBgIsCreativesGenerating(false);
                }).catch(() => {
                    setBackgroundGenerationResults(new Array(3).fill(null));
                    setBgIsCreativesGenerating(false);
                });
            }
        }

        if (isError) {
            setBackgroundGenerationResults(new Array(3).fill(null));
            setBgIsCreativesGenerating(false);
            toast({
                title: "Error",
                status: "error",
                duration: 5000,
                isClosable: true,
                position: "top-right",
            });
        }

        return () => clearTimeout(timeoutId); // Cleanup on unmount
    }, [backgroundImagesStatus, isSuccess, isError, baseImage, backgroundImageIds.length]);

    const onDrop = (acceptedFiles, rejectedFiles) => {

        if (rejectedFiles.length > 0) {
            toast({
                title: "Upload Error",
                description: "The uploaded file exceeds the maximum size limit.",
                status: "error",
                duration: 5000,
                isClosable: true,
                position: "top-right",
            });
            return;
        }

        let validFile = null; // Use let instead of const

        for (const file of acceptedFiles) {
            if (file.type.startsWith("image/") && file.size > MAX_IMAGE_UPLOAD_SIZE_BYTES) {
                toast({
                    title: "Upload Error",
                    description: `The image ${file.name} exceeds the maximum size of 30 MB.`,
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                    position: "top-right",
                });
            } else if (file.type.startsWith("video/") && file.size > MAX_VIDEO_UPLOAD_SIZE_BYTES) {
                toast({
                    title: "Upload Error",
                    description: `The video ${file.name} exceeds the maximum size of 4 GB.`,
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                    position: "top-right",
                });
            } else {
                validFile = {
                    name: file.name,
                    size: file.size,
                    creativeType: file.type,
                    url: URL.createObjectURL(file),
                    file: file,
                };
                break;
            }
        }

        if (validFile) {
            setUploadedImage(validFile);
            setUploadType('local-type');
            setBackgroundGenerationResults([]);
            setCreativeGenerationResults([]);
            setCreativeFiles([]);
            setBackgroundFiles([]);
            setBackgroundImageIds([]);
            setSelectedBackgroundImages([]);
        }
    };

    const handleMediaSelect = (file) => {
        if (file && file.length > 0) {
            setUploadedImage(file[0]);
            setUploadType('drive-type');
            setBackgroundGenerationResults([]);
            setCreativeGenerationResults([]);
            setCreativeFiles([]);
            setBackgroundFiles([]);
            setBackgroundImageIds([]);
            setSelectedBackgroundImages([]);
        }
        onDriveClose();
    };


    const handleUploadAll = async (filesToUpload) => {
        // console.log((window.navigator.onLine ? 'on' : 'off') + 'line');

        window.addEventListener('online', () => {
            toast({
                title: "You are back online!",
                status: "success",
                duration: 4000,
                isClosable: true,
                position: "top-right",
            });
        });

        window.addEventListener('offline', () => {
            toast({
                title: "You are offline. Some functionality may be unavailable.",
                status: "error",
                duration: 4000,
                isClosable: true,
                position: "top-right",
            });
        });

        const uploadPromises = [];

        for (const file of filesToUpload) {

            const adsAccountsArray = selectedAdAccounts.map(account => ({
                id: account.id,
                adsAccountId: account.adsAccountId,
                adsAccountName: account.adsAccountName,
                businessName: account.businessName,
                businessId: account.businessId
            }));

            if (adsAccountsArray.length === 0) {
                toast({
                    title: "Account Error",
                    description: "Please select a valid Ads Account.",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                    position: "top-right",
                });
                return;
            }

            if (file.type.startsWith("image/")) {
                const uploadImagePromise = (async () => {
                    const formData = new FormData();
                    formData.append('filename', file.file);
                    formData.append('aiGenerated', true);
                    formData.append('adsAccounts', JSON.stringify(adsAccountsArray));

                    let progressInterval;

                    try {
                        const uploadDuration = 3000;
                        const interval = 100;
                        const totalSteps = uploadDuration / interval;

                        let progress = 0;
                        progressInterval = setInterval(() => {
                            if (progress < 100) {
                                progress += Math.round(100 / totalSteps);
                                adsAccountsArray.forEach(account => {
                                    dispatch(updateUploadStatus({ fileName: file.name, accountId: account.adsAccountId, accountName: account.adsAccountName, status: "uploading", progress }));
                                });
                            }
                        }, interval);

                        const response = await uploadImage({ payload: formData }).unwrap();
                        // Check if the response contains a "File already exist" message
                        const fileExists = response?.data?.some(item => item?.message === "File already exist");

                        onUploadComplete(response.data);

                        if (fileExists) {
                            toast({
                                title: "Upload Error",
                                description: "File already exists",
                                status: "error",
                                duration: 5000,
                                isClosable: true,
                                position: "top-right",
                            });

                            // Dispatch failed status for the upload
                            adsAccountsArray.forEach(account => {
                                dispatch(updateUploadStatus({
                                    fileName: file.name,
                                    accountId: account.adsAccountId,
                                    accountName: account.adsAccountName,
                                    status: "failed",
                                    progress: 0,
                                }));
                            });
                        } else {
                            // If no error
                            // onSuccess();

                            // Update status to "done" for each account
                            adsAccountsArray.forEach(account => {
                                dispatch(updateUploadStatus({
                                    fileName: file.name,
                                    accountId: account.adsAccountId,
                                    accountName: account.adsAccountName,
                                    status: "done",
                                    progress: 100,
                                }));
                            });
                        }
                        // Clear the interval regardless of success or failure
                        clearInterval(progressInterval);
                    } catch (error) {
                        clearInterval(progressInterval);
                        adsAccountsArray.forEach(account => {
                            dispatch(updateUploadStatus({ fileName: file.name, accountId: account.adsAccountId, accountName: account.adsAccountName, status: "failed", progress: 0 }));
                        });
                        toast({
                            title: "Upload Error",
                            description: error?.data?.message,
                            status: "error",
                            duration: 5000,
                            isClosable: true,
                            position: "top-right",
                        });
                    }
                })();

                uploadPromises.push(uploadImagePromise);
            }
        }

        // Wait for all uploads to complete
        await Promise.all(uploadPromises);
    };


    const handleGeneration = (type) => {
        if (type === 'background-variation') {
            handleRemoveBackground(type);
        } else if (type === 'creative-variation') {
            handleGenerateBackground(type);
        }
    }

    const handleGenerationSelect = (val) => {

        setActiveGenerationType(val);

        if (isCreativesGenerating || isBgCreativesGenerating) return;

        if (val === 'creative-variation') {
            if (creativeGenerationResults?.length > 0) return;

            const formData = new FormData();
            if (uploadType === 'drive-type') {
                formData.append('image_url', uploadedImage?.url);
            } else {
                formData.append('filename', uploadedImage?.file);
            }

            setIsCreativesGenerating(true);
            setCreativeGenerationResults(new Array(3).fill(undefined)); // Fill 3 placeholders

            generatePrompts(formData)
                .unwrap()
                .then(async (response) => {
                    setCreativeGenerationPrompt(response?.data);

                    const promptFormData = new FormData();
                    if (uploadType === 'drive-type') {
                        promptFormData.append('image_url', uploadedImage?.url);
                    } else {
                        promptFormData.append('filename', uploadedImage?.file);
                    }
                    promptFormData.append('prompt', response?.data);

                    const requests = Array.from({ length: 3 }).map((_, index) =>
                        generateCreativeVariations(promptFormData)
                            .unwrap()
                            .then(res => {
                                const image = res?.data?.image ? `data:image/png;base64,${res.data.image}` : null;
                                setCreativeGenerationResults(prev => {
                                    const updated = [...prev];
                                    updated[index] = image;
                                    return updated;
                                });
                            })
                            .catch(() => {
                                setCreativeGenerationResults(prev => {
                                    const updated = [...prev];
                                    updated[index] = null;
                                    return updated;
                                });
                            })
                    );

                    // Wait for all requests to complete before marking generation as done
                    await Promise.allSettled(requests);
                    setIsCreativesGenerating(false);

                })
                .catch((error) => {
                    setIsCreativesGenerating(false);
                    toast({
                        position: "top-right",
                        title: "Error",
                        description: error?.data?.message,
                        status: "error",
                        duration: 4000,
                        isClosable: true,
                    });
                });

        } else if (val === 'background-variation') {
            if (backgroundGenerationResults?.length > 0) return;

            const formData = new FormData();
            if (uploadType === 'drive-type') {
                formData.append('url', uploadedImage?.url);
            } else {
                formData.append('image', uploadedImage?.file);
            }

            setIsBgLoading(true);
            setBgIsCreativesGenerating(true);
            setBackgroundImageIds([]);
            attemptRef.current = 1;
            setBackgroundGenerationResults(new Array(3).fill(undefined)); // Fill 3 placeholders

            removeBackground(formData)
                .unwrap()
                .then(async (response) => {
                    const base64Image = `data:image/png;base64,${response.data.image}`;
                    setBaseImage(base64Image); // <- store base image for overlay
                    const file = base64ToFile(base64Image, undefined, 'image/png');
                    const formData = new FormData();
                    formData.append('filename', file);

                    generateBackgroundPrompt(formData)
                        .unwrap()
                        .then(async (response) => {
                            setBackgroundGenerationPrompt(response?.data);
                            setIsBgLoading(false);

                            const payload = { prompt: response?.data };

                            // Launch 3 parallel calls to generateBackgroundImages
                            const requests = Array.from({ length: 3 }, () =>
                                generateBackgroundImages(payload).unwrap()
                            );

                            // Use allSettled to handle each response independently
                            const settledResponses = await Promise.allSettled(requests);

                            // Collect image IDs from successful responses
                            const imageIds = settledResponses
                                .filter(result => result.status === 'fulfilled' && result.value?.data?.id)
                                .map(result => result.value.data.id); // Get the id directly

                            setBackgroundImageIds(imageIds);
                            setBgIsCreativesGenerating(false);
                        })
                        .catch((error) => {
                            // console.error('Prompt generation error:', error);
                            setBgIsCreativesGenerating(false);
                            setIsBgLoading(false);
                            toast({
                                position: "top-right",
                                title: "Error",
                                description: error?.data?.message,
                                status: "error",
                                duration: 4000,
                                isClosable: true,
                            });
                        });
                })
                .catch((error) => {
                    // console.error('Background generation error:', error);
                    setBgIsCreativesGenerating(false);
                    setIsBgLoading(false);
                    toast({
                        position: "top-right",
                        title: "Error",
                        description: error?.data?.message,
                        status: "error",
                        duration: 4000,
                        isClosable: true,
                    });
                });
        }
    };

    const handleRemoveBackground = async (type) => {

        if (type === 'background-variation') {

            const formData = new FormData();
            if (uploadType === 'drive-type') {
                formData.append('url', uploadedImage?.url);
            } else {
                formData.append('image', uploadedImage?.file);
            }

            setBgIsCreativesGenerating(true);
            setBackgroundGenerationResults(new Array(3).fill(undefined)); // Fill 3 placeholders

            const payload = {
                prompt: backgroundGenerationPrompt,
            }

            // Launch 3 parallel calls to generateBackgroundImages
            const requests = Array.from({ length: 3 }, () =>
                generateBackgroundImages(payload).unwrap()
            );

            // Use allSettled to handle each response independently
            const settledResponses = await Promise.allSettled(requests);

            // Collect image IDs from successful responses
            const imageIds = settledResponses
                .filter(result => result.status === 'fulfilled' && result.value?.data?.id)
                .map(result => result.value.data.id); // Get the id directly

            setBackgroundImageIds(imageIds);
            setBgIsCreativesGenerating(false);

        }
    };

    const handleGenerateBackground = async (type) => {

        setBackgroundImageIds([]); // <-- Clear old image IDs
        attemptRef.current = 1;    // <-- Reset retry count
        setIsCreativesGenerating(true);
        setCreativeGenerationResults(new Array(3).fill(undefined)); // Fill 3 placeholders

        if (type === 'creative-variation') {

            const promptFormData = new FormData();
            if (uploadType === 'drive-type') {
                promptFormData.append('image_url', uploadedImage?.url);
            } else {
                promptFormData.append('filename', uploadedImage?.file);
            }
            promptFormData.append('prompt', creativeGenerationPrompt);

            const requests = Array.from({ length: 3 }).map((_, index) =>
                generateCreativeVariations(promptFormData)
                    .unwrap()
                    .then(res => {
                        const image = res?.data?.image ? `data:image/png;base64,${res.data.image}` : null;
                        setCreativeGenerationResults(prev => {
                            const updated = [...prev];
                            updated[index] = image;
                            return updated;
                        });
                    })
                    .catch(() => {
                        setCreativeGenerationResults(prev => {
                            const updated = [...prev];
                            updated[index] = null;
                            return updated;
                        });
                    })
            );

            // Wait for all requests to complete before marking generation as done
            await Promise.allSettled(requests);
            setIsCreativesGenerating(false);

        }
    };



    const processCreativeBase64ToFiles = (base64Images, uploadedImage) => {
        if (!uploadedImage?.name) return [];

        const refName = uploadedImage.name.split(".")[0];
        const timestamp = Date.now();

        return base64Images
            .filter(Boolean)
            .map((base64, index) => {
                const fileName = `${refName}${timestamp}_${index + 1}.png`;
                const file = base64ToFile(base64, fileName, 'image/png');

                return {
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    file: file,
                    thumbnail: base64,
                    status: 'pending',
                    progress: 0,
                };
            });
    };


    const processImagesToFiles = async (urls, uploadedImage) => {
        if (!uploadedImage?.name) return [];

        const refName = uploadedImage.name.split(".")[0];
        const timestamp = Date.now();

        return await Promise.all(
            urls.map(async (url, index) => {
                const fileName = `${refName}${timestamp}_${index + 1}.png`;
                const file = await urlToFile(url, fileName, 'image/png');

                return {
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    file: file,
                    thumbnail: url,
                    status: 'pending',
                    progress: 0,
                };
            })
        );
    };


    const updateCombinedFiles = (newCreativeFiles = creativeFiles, newBackgroundFiles = backgroundFiles) => {
        const combined = [...newCreativeFiles, ...newBackgroundFiles];
        dispatch(setFiles(combined));
        setLocalFiles(combined);
    };

    const handleBackgroundCheckboxChange = async (url) => {
        const updatedSelection = selectedBackgroundImages.includes(url)
            ? selectedBackgroundImages.filter((item) => item !== url)
            : [...selectedBackgroundImages, url];

        setSelectedBackgroundImages(updatedSelection);

        const processedImages = await processImagesToFiles(updatedSelection, uploadedImage);
        setBackgroundFiles(processedImages);
        updateCombinedFiles(creativeFiles, processedImages);
    };

    const handleSelectAllBackgrounds = async () => {
        const validResults = backgroundGenerationResults.filter(res => res !== null && res !== undefined);

        let updatedSelection = [];

        if (selectedBackgroundImages.length === validResults.length) {
            updatedSelection = [];
        } else {
            updatedSelection = validResults.map(item => item.url);
        }

        setSelectedBackgroundImages(updatedSelection);

        const processedImages = await processImagesToFiles(updatedSelection, uploadedImage);
        setBackgroundFiles(processedImages);
        updateCombinedFiles(creativeFiles, processedImages);
    };


    const handleCreativeCheckboxChange = (url) => {
        const updatedSelection = selectedCreativeImages.includes(url)
            ? selectedCreativeImages.filter((item) => item !== url)
            : [...selectedCreativeImages, url];

        setSelectedCreativeImages(updatedSelection);

        const processedImages = processCreativeBase64ToFiles(updatedSelection, uploadedImage);
        setCreativeFiles(processedImages);
        updateCombinedFiles(processedImages, backgroundFiles);
    };

    const handleSelectAllCreatives = () => {
        let updatedSelection = [];

        if (selectedCreativeImages.length === creativeGenerationResults.length) {
            updatedSelection = [];
        } else {
            updatedSelection = creativeGenerationResults.filter(Boolean); // Remove any undefined/null
        }

        setSelectedCreativeImages(updatedSelection);

        const processedImages = processCreativeBase64ToFiles(updatedSelection, uploadedImage);
        setCreativeFiles(processedImages);
        updateCombinedFiles(processedImages, backgroundFiles);
    };


    const {
        getRootProps,
        getInputProps,
        isDragActive,
        open
    } = useDropzone({
        onDrop,
        accept: {
            "image/*": [".png", ".jpg", ".jpeg"]
        },
        multiple: true,
        maxFiles: 50,
        maxSize: MAX_IMAGE_UPLOAD_SIZE_BYTES,
        noClick: true,
    });

    return (
        <Box h={'100%'} w={'100%'} p={2}>
            {/* Drive Modal */}
            <ReplaceDriveModal
                isDriveModalOpen={isDriveOpen}
                selectedAdAccounts={selectedAdAccounts}
                onClose={onDriveClose}
                onSubmit={handleMediaSelect}
                uploadPurpose="update"
                mediaType={'image'}
            />
            <Flex flex={0.8} minH="400px">
                <Box flex={'33%'} >
                    <Flex direction={'column'} pr={'10px'}>
                        <Text fontWeight={"bold"} fontSize={'lg'} mb={'10px'}>
                            Generation Setup
                        </Text>

                        <FormControl mb={'20px'} display={'flex'} flexDirection={'row'} alignItems={'flex-end'} gap={'10px'}>
                            {uploadedImage && Object.keys(uploadedImage).length > 0 ? (
                                <Flex align="flex-end" gap={4}>
                                    <Flex direction={'column'} gap={2}>
                                        <Image
                                            src={uploadedImage?.url}
                                            alt={uploadedImage?.name}
                                            objectFit="cover"
                                            width="100px"
                                            height="100px"
                                            borderRadius="3px"
                                        />
                                        <Text fontSize={'12px'} color="gray">{truncateName(uploadedImage?.name, 10)}</Text>
                                    </Flex>
                                    <Flex direction="column" gap={1}>
                                        <Text fontSize="12px" fontWeight="medium" color="gray.800">
                                            Replace from
                                        </Text>

                                        <Flex align="center" gap={2}>
                                            <Button
                                                onClick={() => {
                                                    onDriveOpen();
                                                    setCreativeGenerationPrompt('');
                                                    setActiveGenerationType('');
                                                    setBackgroundGenerationResults([]);
                                                    setCreativeGenerationResults([]);
                                                }}
                                                isDisabled={isCreativesGenerating || isBgCreativesGenerating}
                                                variant="link"
                                                p={0}
                                            >
                                                <Text fontSize="12px" color="gray.800">My Drive</Text>
                                            </Button>

                                            <Text fontSize="12px" color="gray.800">or</Text>

                                            <Button
                                                onClick={() => {
                                                    open();
                                                    setCreativeGenerationPrompt('');
                                                    setActiveGenerationType('');
                                                    setBackgroundGenerationResults([]);
                                                    setCreativeGenerationResults([]);
                                                }}
                                                isDisabled={isCreativesGenerating || isBgCreativesGenerating}
                                                variant="link"
                                                p={0}
                                            >
                                                <Text fontSize="12px" color="gray.800">Device</Text>
                                            </Button>
                                        </Flex>
                                    </Flex>
                                </Flex>
                            ) : (
                                <Center
                                    className="dropzone"
                                    {...getRootProps({})}
                                    sx={{
                                        textAlign: 'center',
                                        borderWidth: '1px',
                                        borderColor: 'gray.600',
                                        borderStyle: 'dashed',
                                        background: dropZoneBG,
                                        borderRadius: 10,
                                        minHeight: '120px',
                                        w: '100%',
                                        cursor: 'default',
                                        opacity: 1,
                                    }}
                                    borderColor={isDragActive ? 'gray.500' : 'gray.600'}
                                >
                                    <Input {...getInputProps()} />
                                    <Flex flexDirection={'column'}>
                                        <Text fontSize={'14px'}>Select from</Text>
                                        <Link textDecoration={'underline'} color={'gray.800'} onClick={() => {
                                            onDriveOpen();
                                            setUploadType('drive-type')
                                        }} fontSize={'14px'}>
                                            My Drive
                                        </Link>
                                        <Text fontSize={'14px'}>or</Text>
                                        <Link textDecoration={'underline'} color={'gray.800'} onClick={() => {
                                            open();
                                            setUploadType('local-type')
                                        }} fontSize={'14px'}>
                                            Device
                                        </Link>
                                    </Flex>
                                </Center>
                            )}
                        </FormControl>

                        <RadioGroup
                            mb={'20px'}
                            colorScheme='brand'
                            value={activeGenerationType}
                            onChange={handleGenerationSelect}
                            isDisabled={!uploadedImage || Object.keys(uploadedImage).length === 0}
                        >
                            <Flex direction="column" gap="20px">
                                {/* Background Variation */}
                                <Box>
                                    <Flex gap="10px" alignItems={'center'}>
                                        <Radio bg="#fff" value="background-variation">
                                            <Text fontSize="16px" fontWeight="bold">Background Variation</Text>
                                        </Radio>
                                        <Tooltip
                                            borderRadius={5}
                                            label={
                                                <Flex direction={'column'} gap={2} fontSize="sm" p="5px">
                                                    <Text fontWeight={'bold'}>Background Variation (taken from Meta)</Text>
                                                    <Text>To generate images featuring a specific object in different environments, product image must have a solid or transparent background so that the product image is clear and distinct.
                                                        Should not contain text, partial objects or additional design elements
                                                        File format in JPEG or PNG recommended
                                                        File size no greater than 3,000 x 3,000 (minimum 320 x 320)</Text>
                                                </Flex>
                                            }
                                            fontSize="md"
                                        >
                                            <InfoOutlineIcon pointerEvents="all" />
                                        </Tooltip>
                                    </Flex>
                                    {activeGenerationType === 'background-variation' && hasBgEdit && !isBgLoading && (
                                        <Flex pl="25px" direction="column">
                                            <Text fontSize="12px" mb="5px">Edit Prompt</Text>
                                            <Textarea
                                                fontSize={'12px'}
                                                value={backgroundGenerationPrompt}
                                                onChange={(e) => setBackgroundGenerationPrompt(e.target.value)}
                                                placeholder="Enter your prompt here"
                                                isDisabled={!hasBgEdit || isBgLoading}
                                            />
                                        </Flex>
                                    )}

                                </Box>

                                {/* Creative Variation */}
                                <Box>
                                    <Flex gap="10px" alignItems={'center'}>
                                        <Radio bg="#fff" value="creative-variation">
                                            <Text fontSize="16px" fontWeight="bold">Creative Variation</Text>
                                        </Radio>
                                        <Tooltip
                                            borderRadius={5}
                                            label={
                                                <Flex direction={'column'} gap={2} fontSize="sm" p="5px">
                                                    <Text fontWeight={'bold'}>Creative Variation</Text>
                                                    <Text>This setting generates entirely new images using AI, including both the product and the setting it’s placed in. It allows AI to reinterpret the creative from scratch, offering a fresh, AI-designed visual.</Text>
                                                    <Text>Great when you want to explore completely new creative directions or don't have a specific product image as a base.</Text>
                                                </Flex>
                                            }
                                            fontSize="md"
                                        >
                                            <InfoOutlineIcon pointerEvents="all" />
                                        </Tooltip>
                                    </Flex>
                                    {activeGenerationType === 'creative-variation' && hasVariationEdit && !isCreativesGenerating && (
                                        <Flex pl="25px" direction="column">
                                            <Text fontSize="12px" mb="5px">Edit Prompt</Text>
                                            <Textarea
                                                fontSize={'12px'}
                                                value={creativeGenerationPrompt}
                                                onChange={(e) => setCreativeGenerationPrompt(e.target.value)}
                                                placeholder="Enter your prompt here"
                                                isDisabled={!hasVariationEdit || isCreativesGenerating}
                                            />
                                        </Flex>
                                    )}

                                </Box>
                            </Flex>
                        </RadioGroup>

                        <Flex direction="column" gap={4}>
                            {(activeGenerationType === 'creative-variation') ? (
                                <Button
                                    size="md"
                                    colorScheme='brand'
                                    width="100%"
                                    isDisabled={
                                        !uploadedImage || Object.keys(uploadedImage).length === 0 ||
                                        isCreativesgenerating || isBgCreativesGenerating ||
                                        (!hasVariationEdit && creativeGenerationResults?.length > 0)
                                    }
                                    onClick={() =>
                                        hasVariationEdit
                                            ? handleGeneration(activeGenerationType)
                                            : handleGenerationSelect(activeGenerationType)
                                    }
                                >
                                    Generate
                                </Button>
                            ) : activeGenerationType === 'background-variation' ? (
                                <Button
                                    size="md"
                                    colorScheme='brand'
                                    width="100%"
                                    isDisabled={
                                        !uploadedImage || Object.keys(uploadedImage).length === 0 ||
                                        isCreativesgenerating || isBgCreativesGenerating ||
                                        (!hasBgEdit && backgroundGenerationResults?.length > 0)
                                    }
                                    onClick={() =>
                                        hasBgEdit
                                            ? handleGeneration(activeGenerationType)
                                            : handleGenerationSelect(activeGenerationType)
                                    }
                                >
                                    Generate
                                </Button>
                            ) :
                                <Button
                                    size="md"
                                    colorScheme='brand'
                                    width="100%"
                                    isDisabled
                                >
                                    Generate
                                </Button>
                            }
                        </Flex>

                    </Flex>
                </Box>

                <Box flex={'2%'}>
                    <Divider orientation='vertical' />
                </Box>

                <Box flex={'65%'}>
                    <Flex flexDirection={'column'}>
                        {activeGenerationType == '' ? (
                            null
                        ) : null}

                        {activeGenerationType === 'background-variation' ? (
                            <>
                                {isBgremoving || isBgpromptGenerating ? (
                                <Flex direction={'column'} alignItems="center" justifyContent="center" h="50vh">
                                    <Image
                                        src="/Ai_ImageLoader.gif"
                                        alt="Image Generation Steps"
                                        boxSize="200px"
                                        objectFit="contain"
                                        width={"100%"}
                                    />
                                    <Flex direction={"column"} alignItems={"center"} gap={2}>
                                        <Text fontWeight={"bold"} fontSize={"14px"}>
                                        AI generating variations...
                                        </Text>
                                        <Text fontSize={"14px"}>This step may take up to a minute</Text>
                                    </Flex>
                                </Flex>
                                ) : (
                                    <>
                                        {backgroundGenerationResults?.length > 0 ? (
                                            <>
                                                <Box>
                                                    <Text fontWeight={'bold'} >AI Generated Results</Text>
                                                    <Text fontSize={'12px'}>Select at least one to proceed for use</Text>
                                                </Box>
                                                <Flex justifyContent={'flex-end'}>
                                                    <Checkbox
                                                        colorScheme='brand'
                                                        size="sm"
                                                        variant={"solid"}
                                                        fontSize={'12px'}
                                                        isDisabled={isBgCreativesGenerating}
                                                        isChecked={
                                                            selectedBackgroundImages.length > 0 &&
                                                            selectedBackgroundImages.length ===
                                                            backgroundGenerationResults.filter((res) => res !== null && res !== undefined).length
                                                        }
                                                        onChange={() => handleSelectAllBackgrounds()}
                                                    >
                                                        Select All
                                                    </Checkbox>
                                                </Flex>

                                                <Flex
                                                    flexDirection="row"
                                                    gap="10px"
                                                    mt="10px"
                                                    flexWrap="wrap"
                                                >
                                                    {Array.from({ length: 3 }).map((_, index) => {
                                                        const result = backgroundGenerationResults[index];
                                                        const isLoading = isBgCreativesGenerating && result === undefined;
                                                        const isFailed = result === null;

                                                        return (
                                                            <Box
                                                                key={index}
                                                                role="group"
                                                                borderWidth="1px"
                                                                borderRadius="md"
                                                                overflow="hidden"
                                                                p="2px"
                                                                width="170px"
                                                                height="170px"
                                                                position="relative"
                                                                display="flex"
                                                                alignItems="center"
                                                                justifyContent="center"
                                                                bg="gray.50"
                                                            >
                                                                {isLoading ? (
                                                                    <Skeleton width="100%" height="160px" />
                                                                ) : isFailed ? (
                                                                    <Text fontSize="12px" color="red.500" textAlign="center">
                                                                        Failed to generate
                                                                    </Text>
                                                                ) : result ? (
                                                                    <>
                                                                        <Image
                                                                            src={result.url}
                                                                            alt={`Generated Image ${index + 1}`}
                                                                            objectFit="cover"
                                                                            width="100%"
                                                                            height="160px"
                                                                            borderRadius="3px"
                                                                        />
                                                                        <Checkbox
                                                                            position="absolute"
                                                                            top="5px"
                                                                            right="5px"
                                                                            bg="#fff"
                                                                            isDisabled={isBgCreativesGenerating}
                                                                            isChecked={selectedBackgroundImages.includes(result.url)}
                                                                            onChange={() => handleBackgroundCheckboxChange(result.url)}
                                                                            colorScheme='brand'
                                                                            border="1px"
                                                                            borderColor="gray.50"
                                                                        />
                                                                        <Box
                                                                            position="absolute"
                                                                            bottom="5px"
                                                                            left="5px"
                                                                            opacity="0"
                                                                            _groupHover={{ opacity: 1 }}
                                                                            zIndex="2"
                                                                            bg="#fff"
                                                                            borderRadius="sm"
                                                                        >
                                                                            <Tooltip
                                                                                label={<Image src={result.url} width="300px" />}
                                                                                bg="gray.50"
                                                                                p="1"
                                                                            >
                                                                                <Box>
                                                                                    <IconButton
                                                                                        aria-label="Preview Image"
                                                                                        icon={<FiEye />}
                                                                                        size="xs"
                                                                                        variant="ghost"
                                                                                        color={"gray.600"}
                                                                                        _hover={{
                                                                                            background: 'none',
                                                                                        }}
                                                                                    />
                                                                                </Box>
                                                                            </Tooltip>
                                                                        </Box>
                                                                    </>
                                                                ) : null}
                                                            </Box>
                                                        );
                                                    })}
                                                </Flex>

                                                <Box mt={'20px'}>
                                                    <Box mb={'10px'}>
                                                        <Text fontWeight={'bold'} fontSize={'12px'}>Like what you see but not sure yet?</Text>
                                                        <Text fontSize={'12px'}>Click Generate to see more similar backgrounds.</Text>
                                                    </Box>
                                                    <Box cursor={isBgCreativesGenerating || isCreativesGenerating ? "not-allowed" : "pointer"}>
                                                        <Text fontWeight="bold" fontSize="12px">
                                                            Don't like the results?
                                                        </Text>
                                                        <Text fontSize="12px">
                                                            Click{" "}
                                                            <Link
                                                                onClick={() => {
                                                                    if (!isBgCreativesGenerating) {
                                                                        setBgHasEdit(true);
                                                                    }
                                                                }}
                                                                fontWeight="bold"
                                                                color={isBgCreativesGenerating || isCreativesGenerating ? "gray.400" : "none"}
                                                                _hover={{ textDecoration: isBgCreativesGenerating || isCreativesGenerating ? "none" : "underline" }}
                                                                pointerEvents={isBgCreativesGenerating || isCreativesGenerating ? "none" : "auto"}
                                                            >
                                                                here
                                                            </Link>{" "}
                                                            to edit the prompt based on your requirements and click Generate.
                                                        </Text>
                                                    </Box>
                                                </Box>
                                            </>
                                        ) :
                                            // <Flex>
                                            //      <Text fontSize={'14px'}>No images could be generated for this request. Please replace the other image and generating again.</Text>
                                            // </Flex>
                                            null
                                        }
                                    </>
                                )}
                            </>
                        ) : null}

                        {activeGenerationType === 'creative-variation' ? (
                            <>
                                {isCreativesGenerating ? (
                                    <Flex direction={'column'} alignItems="center" justifyContent="center" h="50vh">
                                        <Image
                                            src="/Ai_ImageLoader.gif"
                                            alt="Image Generation Steps"
                                            boxSize="200px"
                                            objectFit="contain"
                                            width={"100%"}
                                        />
                                        <Flex direction={"column"} alignItems={"center"} gap={2}>
                                            <Text fontWeight={"bold"} fontSize={"14px"}>
                                            AI generating variations...
                                            </Text>
                                            <Text fontSize={"14px"}>This step may take 2 to 3 minutes</Text>
                                        </Flex>
                                    </Flex>
                                ) : (
                                    <>
                                        {creativeGenerationResults.length > 0 ? (
                                            <>
                                                <Box>
                                                    <Text fontWeight="bold">AI Generated Results</Text>
                                                    <Text fontSize="12px">Select at least one to proceed for use</Text>
                                                </Box>

                                                <Flex justifyContent="flex-end" mb="10px">
                                                    <Checkbox
                                                        colorScheme='brand'
                                                        size="sm"
                                                        variant="solid"
                                                        fontSize="12px"
                                                        isDisabled={isCreativesgenerating}
                                                        isChecked={
                                                            selectedCreativeImages.length > 0 &&
                                                            selectedCreativeImages.length ===
                                                            creativeGenerationResults.filter((res) => res !== null).length
                                                        }
                                                        onChange={handleSelectAllCreatives}
                                                    >
                                                        Select All
                                                    </Checkbox>
                                                </Flex>

                                                <Flex
                                                    flexDirection="row"
                                                    gap="10px"
                                                    mt="10px"
                                                    flexWrap="wrap"
                                                >
                                                    {Array.from({ length: 3 }).map((_, index) => {
                                                        const result = creativeGenerationResults[index];
                                                        const isLoading = isCreativesGenerating && result === undefined;
                                                        const isFailed = result === null;

                                                        return (
                                                            <Box
                                                                key={index}
                                                                role="group"
                                                                borderWidth="1px"
                                                                borderRadius="md"
                                                                overflow="hidden"
                                                                p="2px"
                                                                width="170px"
                                                                height="170px"
                                                                position="relative"
                                                                display="flex"
                                                                alignItems="center"
                                                                justifyContent="center"
                                                                bg="gray.50"
                                                            >
                                                                {isLoading ? (
                                                                    <Skeleton width="100%" height="160px" />
                                                                ) : isFailed ? (
                                                                    <Text fontSize="12px" color="red.500" textAlign="center">
                                                                        Failed to generate
                                                                    </Text>
                                                                ) : result ? (
                                                                    <>
                                                                        <Image
                                                                            src={result}
                                                                            alt={`Generated Image ${index + 1}`}
                                                                            objectFit="cover"
                                                                            width="100%"
                                                                            height="160px"
                                                                            borderRadius="3px"
                                                                        />
                                                                        <Checkbox
                                                                            position="absolute"
                                                                            top="5px"
                                                                            right="5px"
                                                                            bg="#fff"
                                                                            isDisabled={isCreativesgenerating}
                                                                            isChecked={selectedCreativeImages.includes(result)}
                                                                            onChange={() => handleCreativeCheckboxChange(result)}
                                                                            colorScheme='brand'
                                                                            border="1px"
                                                                            borderColor="gray.50"
                                                                        />
                                                                        <Box
                                                                            position="absolute"
                                                                            bottom="5px"
                                                                            left="5px"
                                                                            opacity="0"
                                                                            _groupHover={{ opacity: 1 }}
                                                                            zIndex="2"
                                                                            bg="#fff"
                                                                            borderRadius="sm"
                                                                        >
                                                                            <Tooltip
                                                                                label={<Image src={result} width="300px" />}
                                                                                bg="gray.50"
                                                                                p="1"
                                                                            >
                                                                            <IconButton
                                                                                aria-label="Preview Image"
                                                                                icon={<FiEye />}
                                                                                size="xs"
                                                                                variant="ghost"
                                                                                color={"gray.600"}
                                                                                _hover={{
                                                                                    background: 'none',
                                                                                }}
                                                                            />
                                                                            </Tooltip>
                                                                        </Box>
                                                                    </>
                                                                ) : null}
                                                            </Box>
                                                        );
                                                    })}
                                                </Flex>

                                                <Box mt="20px">
                                                    <Box mb="10px">
                                                        <Text fontWeight="bold" fontSize="12px">
                                                            Like what you see but not sure yet?
                                                        </Text>
                                                        <Text fontSize="12px">
                                                            Click Generate to see more similar backgrounds.
                                                        </Text>
                                                    </Box>
                                                    <Box cursor={isCreativesGenerating || isBgCreativesGenerating ? "not-allowed" : "pointer"}>
                                                        <Text fontWeight="bold" fontSize="12px">
                                                            Don't like the results?
                                                        </Text>
                                                        <Text fontSize="12px">
                                                            Click{" "}
                                                            <Link
                                                                onClick={() => {
                                                                    if (!isCreativesGenerating) {
                                                                        setVariationHasEdit(true);
                                                                    }
                                                                }}
                                                                fontWeight="bold"
                                                                color={isCreativesGenerating || isBgCreativesGenerating ? "gray.400" : "none"}
                                                                _hover={{ textDecoration: isCreativesGenerating || isBgCreativesGenerating ? "none" : "underline" }}
                                                                pointerEvents={isCreativesGenerating || isBgCreativesGenerating ? "none" : "auto"}
                                                            >
                                                                here
                                                            </Link>{" "}
                                                            to edit the prompt based on your requirements and click Generate.
                                                        </Text>
                                                    </Box>
                                                </Box>
                                            </>
                                        ) : null}
                                    </>
                                )}
                            </>
                        ) : null}
                    </Flex>
                </Box>
            </Flex>

            <Flex flex={0.2} justifyContent="space-between" alignItems="center" py={2}>
                {/* Left Side */}
                <Flex alignItems="center" gap="8px">
                    <InfoOutlineIcon fontSize="12px" />
                    <Text fontSize="12px">
                        AI-generated images will be created in the same dimensions as the uploaded image and will be stored only in the "My Drive" of the designated Ad Accounts.
                    </Text>
                </Flex>
                <Flex gap={2}>
                    <Button
                        colorScheme='brand'
                        size="sm"
                        isDisabled={localFiles?.length === 0 || isCreativesGenerating || isBgCreativesGenerating}
                        onClick={() => {
                            handleUploadAll(localFiles);
                            dispatch(setIsWidgetOpen(true));
                            onClose();
                        }}
                    >
                        Save
                    </Button>
                </Flex>
            </Flex>
        </Box>
    )
}

export default AiImageModal